"use client";

import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { useMemo } from "react";
import slideCalculator, {
  singleSlideCalculator,
} from "@/helpers/testimonialSlideCalculator";
import cn from "clsx";
import styles from "./index.module.scss";
import Image from "next/image";
import { IModalActions, ModalTypes } from "@/types/modal";
import { ModalButton } from "@/components/atoms/ModalButton";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { usePathname } from "next/navigation";

function TestimonialComponent({
  items,
  reverse,
}: {
  reverse?: string;
  items: Testimonial;
}) {
  return (
    <div
      className={cn(
        reverse && "flex-row-reverse",
        "border border-primary-blue rounded-2xl text-primary-blue lg:flex h-full overflow-hidden",
      )}
    >
      <div
        className={cn(
          "mx-auto overflow-hidden lg:border-r-0 lg:border-l basis-[35%]",
          styles.cardImageContainer,
        )}
      >
        {items?.attributes?.Picture && (
          <Image
            src={items?.attributes?.Picture?.source.data[0].attributes.url}
            alt={
              items?.attributes?.Picture?.source.data[0].attributes
                .alternativeText || "Students Image"
            }
            title="Students Image"
            width={270}
            height={300}
            className={cn("object-cover w-full h-full", styles.cardImage)}
          />
        )}
      </div>
      <div
        className={cn(
          "lg:flex lg:flex-col lg:justify-between h-full basis-[65%]",
          styles.cardContent,
        )}
      >
        <p
          className={cn(
            "lg:text-xl font-medium font-raleway",
            styles.shortDesc,
          )}
        >
          "{items?.attributes?.ShortDesc}"
        </p>
        <div>
          <h3 className={cn("font-baskervville", styles.name)}>
            {items?.attributes?.Name}
          </h3>
          <p className={cn("font-raleway opacity-80", styles.desg)}>
            {items?.attributes?.Department ? `${items?.attributes?.Department},` : ""} {items?.attributes?.Designation}
          </p>
        </div>
      </div>
    </div>
  );
}

function VideoTestimonialComponone({ items, sectionHeading=""}: { items: VideoTestimonials, sectionHeading : string}) {
const isAfricaPage = usePathname().split("/")[1] == "africa";  
const africaStyle = isAfricaPage ? (items?.attributes?.Name=="Nattimba Rachel"?"object-[50%_20%]":"object-top") : ""

  return (
    <div className="border border-primary-blue rounded-2xl bg-blue-linear-gradient h-full  xl:overflow-hidden">
      <figure className="relative h-full">
        <Image
          src={items?.attributes?.VideoEmbed.PosterImage?.data?.attributes?.url}
          alt={
            items?.attributes?.VideoEmbed.PosterImage?.data?.attributes?.alternativeText || "Image"
          }
          fill
          className={cn(
            `object-cover w-full h-full rounded-2xl mix-blend-multiply ${africaStyle}`,
            styles.videoImg
          )}
        />
        <figcaption className="absolute bottom-0 left-0 w-full">
          <div className={cn("flex items-end justify-between", styles.details)}>
            <div>
              <h2
                className={cn(
                  "[text-shadow: 0px 4.362116813659668px 4.362116813659668px rgba(0, 0, 0, 0.25)]",
                  styles.name
                )}
              >
                {items?.attributes?.Name}
              </h2>
              <p className={cn(styles.desg)}>
                {items?.attributes?.Department ? `${items?.attributes?.Department},`: ""} {items?.attributes?.Designation}
              </p>
            </div>
            <ModalButton
              handleClickCB={() => {
                pushToDataLayer({
                  event: "play_video_icon_click",
                  cta_text: "Play Video",
                  section_name: sectionHeading || "NA",
                  page_type: getGlobalStateValue("pageType") || "Home Page",
                });
              }}
              action={IModalActions.OpenModal}
              data={{
                modalType: ModalTypes.VideoPopup,
                modalData: {
                  url: items?.attributes?.VideoEmbed?.enabled
                    ? items?.attributes?.VideoEmbed?.URL
                    : "",
                },
              }}
            >
              <img
                src="https://amityonline.com/_s/play_icon1_27947fe221.webp"
                alt={"Student Play Icon"}
                className="h-[61px] w-[60px] hidden lg:inline-block cursor-pointer homeSprite"
                loading="lazy"
              />
            </ModalButton>
          </div>
        </figcaption>
      </figure>
    </div>
  );
}

const SliderContent = ({ slide1, slide2, reverse, sectionHeading }: any) => {
  if (!slide1 || !slide2) return null;

  return (
    <div
      className={cn(
        "flex justify-between",
        reverse && "flex-row-reverse",
        styles.testimonialBox,
      )}
    >
      <div className={cn("basis-[65%]", styles.card)}>
        <TestimonialComponent reverse={reverse} items={slide1} />
      </div>
      <div className={cn("basis-[33.5%]", styles.video)}>
        <VideoTestimonialComponone sectionHeading={sectionHeading} items={slide2} />
      </div>
    </div>
  );
};

const SingleSliderContent = ({ slide1, slide2, reverse }: any) => {
  if (!slide1 || !slide2) return null;

  return (
    <div
      className={cn(
        "flex justify-between gap-4",
        reverse && "flex-row-reverse",
        styles.testimonialBox,
      )}
    >
      <div className={cn("basis-1/2", styles.card)}>
        <TestimonialComponent reverse={reverse} items={slide1} />
      </div>
      <div className={cn("basis-1/2", styles.video)}>
        <TestimonialComponent items={slide2} reverse={reverse} />
      </div>
    </div>
  );
};

export function StudentsGridCarousel({
  studentsDetails,
  videoTestimonials,
  isSingleSlider = false,
  sectionHeading=""
}: {
  studentsDetails: Testimonial[];
  videoTestimonials: VideoTestimonials[];
  isSingleSlider?: boolean;
  sectionHeading?:string;
}) {
  const testimonials = useMemo(() => {
    if (isSingleSlider) {
      return singleSlideCalculator(studentsDetails).arrayToRender as never;
    }
    return slideCalculator(studentsDetails, videoTestimonials)
      .arrayToRender as never;
  }, []);

  return (
    <div className={styles.root}>
      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={16}
        autoplay={{
          delay: 6000,
        }}
        slidesPerView={1}
        centeredSlides
        wrapperClass={styles.wrapper}
        pagination={{ clickable: true, bulletElement: "div" }}
        className={cn("Carousel__blueBullet", styles.carousel)}
      >
        {(testimonials as any[]).map((item: any, i: number) => (
          <SwiperSlide key={i}>
            <div key={i} className="flex flex-col">
              <SliderContent sectionHeading={sectionHeading} slide1={item[0]} slide2={item[2]} />
              <SliderContent sectionHeading={sectionHeading} slide2={item[3]} slide1={item[1]} reverse />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export function StudentsTestimonialsGridCarousel({
  studentsDetails,
}: {
  studentsDetails: Testimonial[];
}) {
  const testimonials = useMemo(() => {
    return singleSlideCalculator(studentsDetails).arrayToRender as never;
  }, []);

  return (
    <div className={styles.root}>
      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={16}
        autoplay={{
          delay: 6000,
        }}
        slidesPerView={1}
        centeredSlides
        wrapperClass={styles.wrapper}
        pagination={{ clickable: true, bulletElement: "div" }}
        className={cn("Carousel__blueBullet", styles.carousel)}
      >
        {(testimonials as any[]).map((item: any, i: number) => (
          <SwiperSlide key={i}>
            <div key={i} className="flex flex-col">
              <SingleSliderContent slide1={item[0]} slide2={item[2]} />
              <SingleSliderContent slide2={item[3]} slide1={item[1]} reverse />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
